import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, Milk } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { milk_type: "cow", effective_date: today(), base_rate: "", fat_rate: "", snf_rate: "" };

export default function RateChart() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const { data, isLoading } = useQuery({ queryKey: ["rates"], queryFn: async () => (await api.get("/rate-charts")).data });

  const save = useMutation({
    mutationFn: async () => (await api.post("/rate-charts", {
      ...form, base_rate: Number(form.base_rate), fat_rate: Number(form.fat_rate), snf_rate: Number(form.snf_rate),
    })).data,
    onSuccess: () => { toast.success("Rate chart added"); setOpen(false); setForm(EMPTY); qc.invalidateQueries({ queryKey: ["rates"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const cow = (data || []).filter((r) => r.milk_type === "cow");
  const buffalo = (data || []).filter((r) => r.milk_type === "buffalo");

  return (
    <div className="space-y-6" data-testid="ratechart-page">
      <PageHeader
        title={t("rate.title")}
        subtitle={t("rate.subtitle")}
        breadcrumb={[t("crumb.center"), t("rate.title")]}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-rate-btn"><Plus className="mr-1.5 h-4 w-4" /> {t("rate.new")}</Button></DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle className="font-heading">{t("rate.new")}</DialogTitle></DialogHeader>
              <div className="grid gap-4 py-2 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>{t("col.milkType")}</Label>
                  <Select value={form.milk_type} onValueChange={set("milk_type")}>
                    <SelectTrigger data-testid="rate-milktype-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="cow">{t("common.cow")}</SelectItem><SelectItem value="buffalo">{t("common.buffalo")}</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>{t("rate.effectiveDate")}</Label><Input type="date" value={form.effective_date} onChange={set("effective_date")} data-testid="rate-date-input" /></div>
                <div className="space-y-1.5"><Label>{t("rate.base")}</Label><Input type="number" step="0.1" value={form.base_rate} onChange={set("base_rate")} data-testid="rate-base-input" /></div>
                <div className="space-y-1.5"><Label>{t("rate.fat")}</Label><Input type="number" step="0.1" value={form.fat_rate} onChange={set("fat_rate")} data-testid="rate-fat-input" /></div>
                <div className="space-y-1.5"><Label>{t("rate.snf")}</Label><Input type="number" step="0.1" value={form.snf_rate} onChange={set("snf_rate")} data-testid="rate-snf-input" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
                <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="submit-rate-btn">{save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("common.add")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {isLoading ? (
        <div className="grid gap-4 lg:grid-cols-2"><Skeleton className="h-64" /><Skeleton className="h-64" /></div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {[["Cow", cow, t("rate.cowRates")], ["Buffalo", buffalo, t("rate.buffaloRates")]].map(([label, rows, title]) => (
            <Card key={label}>
              <CardHeader><CardTitle className="flex items-center gap-2 font-heading text-lg"><Milk className="h-5 w-5 text-primary" /> {title}</CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead>{t("rate.effectiveDate")}</TableHead><TableHead className="text-right">{t("plans.storage") && "Base"}</TableHead><TableHead className="text-right">FAT</TableHead><TableHead className="text-right">SNF</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {rows.map((r, i) => (
                      <TableRow key={r.id} data-testid={`rate-row-${label.toLowerCase()}-${i}`}>
                        <TableCell>{r.effective_date} {i === 0 && <Badge className="ml-1.5" variant="outline">{t("common.current")}</Badge>}</TableCell>
                        <TableCell className="text-right tnum">₹{r.base_rate}</TableCell>
                        <TableCell className="text-right tnum">₹{r.fat_rate}</TableCell>
                        <TableCell className="text-right tnum">₹{r.snf_rate}</TableCell>
                      </TableRow>
                    ))}
                    {rows.length === 0 && <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">{t("rate.none")}</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
